import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatMadridDateTime } from "@/lib/datetime";
import { toast } from "sonner";
import { Download, Upload, Database, RefreshCw, Trash2, ShieldAlert, FileArchive, Clock, CheckCircle2, XCircle } from "lucide-react";

/**
 * Pestaña Backup en Configuración (solo admin).
 *
 * Funcionalidad:
 *  - "Descargar backup ahora": GET /api/admin/backup/export → descarga gzip live.
 *  - "Crear snapshot en servidor": POST /api/admin/backup/snapshot → lo añade a la lista.
 *  - Lista de backups en disco con tamaño, fecha y botones: descargar / eliminar.
 *  - Sección "Restaurar" (peligrosa): selector de archivo + modo (merge|replace) +
 *    inputs de confirmación literal ("RESTAURAR" y, si replace, "REEMPLAZAR").
 *
 * NOTA: la BD MongoDB de Emergent ya es persistente entre deploys. Este
 * sistema es una protección extra contra migraciones manuales o errores
 * humanos. El usuario reportó haber perdido datos en una migración previa.
 */
export default function BackupTab() {
  const [items, setItems] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  // Restore form
  const fileInput = useRef(null);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreMode, setRestoreMode] = useState("merge");
  const [confirm, setConfirm] = useState("");
  const [confirmReplace, setConfirmReplace] = useState("");
  const [restoring, setRestoring] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [listRes, schedRes] = await Promise.all([
        api.get("/admin/backup/list"),
        api.get("/admin/backup/schedule"),
      ]);
      setItems(listRes.data?.items || []);
      setSchedule(schedRes.data || null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const downloadLive = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/backup/export", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/gzip" });
      const url = URL.createObjectURL(blob);
      const ts = new Date().toISOString().replace(/[-:]/g, "").slice(0, 15);
      const a = document.createElement("a");
      a.href = url;
      a.download = `backup-${ts}.json.gz`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup descargado");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const snapshot = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/backup/snapshot");
      toast.success(`Snapshot creado: ${r.data.name}`);
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const downloadOne = (name) => {
    // Descarga directa preservando cookies de sesión.
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/backup/download/${encodeURIComponent(name)}`;
    window.open(url, "_blank");
  };

  const deleteOne = async (name) => {
    if (!window.confirm(`¿Eliminar definitivamente "${name}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/backup/${encodeURIComponent(name)}`);
      toast.success("Backup eliminado");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    if (!restoreFile) { toast.error("Selecciona un archivo de backup"); return; }
    if (confirm !== "RESTAURAR") { toast.error("Escribe RESTAURAR para confirmar"); return; }
    if (restoreMode === "replace" && confirmReplace !== "REEMPLAZAR") {
      toast.error("Para reemplazar escribe REEMPLAZAR"); return;
    }
    setRestoring(true);
    try {
      const form = new FormData();
      form.append("file", restoreFile);
      form.append("mode", restoreMode);
      form.append("confirm", confirm);
      if (restoreMode === "replace") form.append("confirm_replace", confirmReplace);
      const r = await api.post("/admin/backup/restore", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Restaurado (snapshot previo: ${r.data.pre_snapshot})`);
      setRestoreFile(null);
      setConfirm("");
      setConfirmReplace("");
      if (fileInput.current) fileInput.current.value = "";
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRestoring(false);
    }
  };

  const fmtSize = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(2)} MB`;
  };
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("es", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };
  const fmtDur = (s) => {
    if (s == null) return "—";
    if (s < 60) return `${Number(s).toFixed(s < 10 ? 1 : 0)} s`;
    const m = Math.floor(s / 60);
    return `${m}m ${Math.round(s % 60)}s`;
  };

  return (
    <div className="space-y-6">
      {/* Acciones manuales */}
      <div className="ds-card">
        <div className="px-4 py-3 border-b border-[#E5E5E0] flex items-center gap-2 font-display font-bold text-sm">
          <Database size={16} /> Copias de seguridad
        </div>
        <div className="p-4 space-y-3">
          <div className="text-xs text-[#525252]">
            La base de datos hace una copia automática diaria. Aquí puedes generar una al instante
            o descargar las existentes.
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={downloadLive}
              disabled={busy}
              data-testid="backup-download-live"
              className="inline-flex items-center gap-2 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
            >
              <Download size={14} /> Descargar backup ahora
            </button>
            <button
              onClick={snapshot}
              disabled={busy}
              data-testid="backup-snapshot"
              className="inline-flex items-center gap-2 bg-[#111110] hover:bg-black text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
            >
              <FileArchive size={14} /> Crear snapshot en servidor
            </button>
            <button
              onClick={refresh}
              disabled={loading}
              data-testid="backup-refresh"
              className="inline-flex items-center gap-2 border border-[#E5E5E0] hover:border-[#111110] px-3 py-2 text-sm disabled:opacity-50"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Actualizar
            </button>
          </div>
        </div>
      </div>

      {/* Programación automática (Iter 185) */}
      <div className="ds-card" data-testid="backup-schedule-card">
        <div className="px-4 py-3 border-b border-[#E5E5E0] flex items-center gap-2 font-display font-bold text-sm">
          <Clock size={16} /> Programación automática
        </div>
        <div className="p-4 space-y-4">
          <div className="text-xs text-[#525252]">
            El servidor realiza <strong>2 copias automáticas al día</strong> en zona horaria{" "}
            <strong>Europe/Madrid</strong> (horario de verano/invierno automático). No depende de que
            el CRM esté abierto en ningún navegador y sigue activo tras reinicios y despliegues.
          </div>
          <div className="flex flex-wrap gap-2">
            {(schedule?.slots || [{ label: "15:00" }, { label: "23:59" }]).map((s) => (
              <span
                key={s.label}
                data-testid={`backup-slot-${s.label}`}
                className="inline-flex items-center gap-2 border border-[#111110] bg-white px-3 py-1.5 text-sm font-mono"
              >
                <Clock size={13} /> {s.label}
                {s.done_today && <CheckCircle2 size={13} className="text-[#6E8B3D]" title="Ejecutada hoy" />}
              </span>
            ))}
            {schedule?.next_run_local && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#525252] font-mono">
                Próxima: {formatMadridDateTime(schedule.next_run_local)}
              </span>
            )}
          </div>

          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-2">
              Registro de ejecuciones (últimas 30)
            </div>
            <div className="border border-[#E5E5E0] overflow-x-auto">
              <table className="w-full text-xs" data-testid="backup-runs-table">
                <thead>
                  <tr className="bg-[#F7F7F4] text-left font-mono uppercase tracking-wider text-[10px] text-[#525252]">
                    <th className="px-3 py-2">Fecha y hora</th>
                    <th className="px-3 py-2">Estado</th>
                    <th className="px-3 py-2">Duración</th>
                    <th className="px-3 py-2">Tamaño</th>
                    <th className="px-3 py-2">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {(schedule?.recent_runs || []).length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-4 text-center text-[#525252]">
                      Aún no hay ejecuciones registradas.
                    </td></tr>
                  )}
                  {(schedule?.recent_runs || []).map((run) => {
                    const ok = run.status === "success";
                    const err = run.status === "error";
                    return (
                      <tr key={run.id} className="border-t border-[#E5E5E0]" data-testid={`backup-run-${run.id}`}>
                        <td className="px-3 py-2 font-mono whitespace-nowrap">{formatMadridDateTime(run.started_at)}</td>
                        <td className="px-3 py-2">
                          {ok && <span className="inline-flex items-center gap-1 text-[#3F5222]"><CheckCircle2 size={12} /> Correcta</span>}
                          {err && <span className="inline-flex items-center gap-1 text-red-700"><XCircle size={12} /> Error</span>}
                          {!ok && !err && <span className="text-[#525252]">{run.status}</span>}
                        </td>
                        <td className="px-3 py-2 font-mono">{fmtDur(run.duration_seconds)}</td>
                        <td className="px-3 py-2 font-mono">{run.size_bytes ? fmtSize(run.size_bytes) : "—"}</td>
                        <td className="px-3 py-2 text-[#525252] max-w-[280px] truncate" title={run.error || ""}>
                          {err ? (run.error || "Error") : (run.slot ? `Slot ${run.slot.slice(0, 2)}:${run.slot.slice(2)}` : "—")}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de snapshots */}
      <div className="ds-card">
        <div className="px-4 py-3 border-b border-[#E5E5E0] font-display font-bold text-sm">
          Snapshots en servidor ({items.length})
        </div>
        <div>
          {items.length === 0 && (
            <div className="p-6 text-center text-sm text-[#525252]">
              Aún no hay snapshots. Crea uno con el botón superior, o espera al backup automático.
            </div>
          )}
          {items.map((it) => (
            <div
              key={it.name}
              className="px-4 py-3 border-b border-[#E5E5E0] last:border-0 flex items-center justify-between gap-3"
              data-testid={`backup-item-${it.name}`}
            >
              <div className="min-w-0">
                <div className="font-mono text-xs font-semibold truncate">{it.name}</div>
                <div className="text-[10px] text-[#525252]">
                  {fmtDate(it.modified_at)} · {fmtSize(it.size)}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => downloadOne(it.name)}
                  data-testid={`backup-download-${it.name}`}
                  className="inline-flex items-center gap-1 border border-[#E5E5E0] hover:border-[#111110] px-3 py-1.5 text-xs"
                >
                  <Download size={12} /> Descargar
                </button>
                <button
                  onClick={() => deleteOne(it.name)}
                  data-testid={`backup-delete-${it.name}`}
                  className="inline-flex items-center gap-1 border border-red-300 hover:bg-red-50 text-red-700 px-3 py-1.5 text-xs"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Restore */}
      <div className="bg-amber-50 border border-amber-300">
        <div className="px-4 py-3 border-b border-amber-300 flex items-center gap-2 font-display font-bold text-sm text-amber-900">
          <ShieldAlert size={16} /> Restaurar copia de seguridad
        </div>
        <div className="p-4 space-y-3">
          <div className="text-xs text-amber-900">
            ⚠️ Operación delicada. El servidor crea automáticamente un snapshot previo antes de
            restaurar, pero conviene tener un respaldo descargado manualmente. Modo recomendado:
            <strong> Fusionar (merge)</strong>.
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-amber-900 block mb-1">
              Archivo de backup (.json.gz)
            </label>
            <input
              ref={fileInput}
              type="file"
              accept=".gz,.json,application/gzip,application/json"
              onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
              data-testid="backup-restore-file"
              className="block w-full text-xs bg-white border border-amber-300 px-2 py-1.5"
            />
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-amber-900 block mb-1">
              Modo
            </label>
            <div className="flex gap-3 text-sm">
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="radio" value="merge" checked={restoreMode === "merge"}
                  onChange={(e) => setRestoreMode(e.target.value)}
                  data-testid="backup-mode-merge"
                />
                <span><strong>Fusionar</strong> (upsert por id, seguro)</span>
              </label>
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="radio" value="replace" checked={restoreMode === "replace"}
                  onChange={(e) => setRestoreMode(e.target.value)}
                  data-testid="backup-mode-replace"
                />
                <span><strong className="text-red-700">Reemplazar</strong> (drop + insert, destructivo)</span>
              </label>
            </div>
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-amber-900 block mb-1">
              Escribe <code className="bg-amber-200 px-1">RESTAURAR</code> para confirmar
            </label>
            <input
              type="text" value={confirm} onChange={(e) => setConfirm(e.target.value)}
              data-testid="backup-confirm"
              placeholder="RESTAURAR"
              className="w-full bg-white border border-amber-300 focus:border-amber-600 outline-none px-3 py-2 text-sm rounded-none font-mono"
            />
          </div>

          {restoreMode === "replace" && (
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-red-700 block mb-1">
                Escribe <code className="bg-red-100 px-1">REEMPLAZAR</code> para confirmar reemplazo destructivo
              </label>
              <input
                type="text" value={confirmReplace} onChange={(e) => setConfirmReplace(e.target.value)}
                data-testid="backup-confirm-replace"
                placeholder="REEMPLAZAR"
                className="w-full bg-white border border-red-400 focus:border-red-600 outline-none px-3 py-2 text-sm rounded-none font-mono"
              />
            </div>
          )}

          <button
            onClick={restore}
            disabled={restoring || !restoreFile}
            data-testid="backup-restore-submit"
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold disabled:opacity-50 ${
              restoreMode === "replace"
                ? "bg-red-700 hover:bg-red-800 text-white"
                : "bg-[#6E8B3D] hover:bg-[#556C2E] text-white"
            }`}
          >
            <Upload size={14} /> {restoring ? "Restaurando..." : `Restaurar (${restoreMode})`}
          </button>
        </div>
      </div>
    </div>
  );
}
